import type { Dispatch } from 'redux'
import type { MapById, Product } from 'shared/store/ecommerce/catalog'
import {
    ecommOrderToCart,
    fetchCatalog,
    getItemsFromIds,
    getProductById
} from 'shared/store/ecommerce/catalog'
import endpoints from 'shared/store/endpoints'
import { untransformPoints } from 'shared/store/kingdom/points'
import type { ActionUpdateOrder as ActionUpdateOrb } from 'shared/store/orb'
import { handleCouponMessageError } from 'shared/store/kingdom/privileges/actions'
import type { RootState } from 'shared/store/reducers'
import { fetchRestaurantData } from 'shared/store/restaurant'
import { deliveryClient, ws } from 'shared/store/ws'
import type {
    BrowserInfo,
    CacPromotion,
    ClickAndCollectPromotion,
    Coupon,
    DeliveryFees,
    DeviceCheck,
    GameCoupon,
    OrderJobDriverResponse,
    OrderRestaurantShortDTO,
    PaymentMethod,
    PriceEdgeItem,
    ProfileUserForm,
    StoredPaymentMethod
} from 'shared/types'
import {
    catalogType,
    COUPON_CODES,
    couponOrigin,
    customTimeout,
    ecommerceType,
    ecommOrderStatus,
    errors,
    isDefined,
    orderPickUpType,
    orderStatus,
    promotionBeginningType,
    promotionOriginType,
    promoType
} from 'shared/utils'
import {
    findProductInCart,
    getCurrentOrder,
    getEcommerceAxiosClient,
    getMostRecentOrder,
    getOrderPrice,
    isEcommerceModeActive,
    isKingdomPointsCoupon,
    isLotteryCoupon,
    isTuPreferesKingCoupon,
    operationGameToPurchasable,
    promotionsAppliedInCart,
    purchasable,
    purchasableToCacOrderContent,
    selectDeliveryFees
} from 'shared/utils/ecommerce'
import { getCoordinatesFromWoosmap } from 'shared/utils/getGeocoding'
import {
    OPERATION_REPEAT_ATTRIBUTION_TYPE,
    promotionEmailCouponType
} from '../../../utils'
import { pointsSelector, priceSelector } from './orderSelectors'
import type {
    EcommOrder,
    FetchOrdersParams,
    ItemInfos,
    MissingIds,
    OrderInfos,
    PaymentResponse,
    ProcessCoupon,
    PurchasableItem,
    SendOrderPrepare
} from './types'
import { findLinkedProduct } from './utils'
import { isDeliveryOrderPaid } from '../../../utils/ecommerce'

const { GAME, MENU, ORDER, PRODUCT } = promoType
const {
    KINGDOM_POINTS,
    OPERATION_REPEAT_COUPON,
    LOTTERY_OPERATION,
    COUPON_MAIL,
    GENERIC_COUPON
} = couponOrigin
const { ACCEPTED, WAITING_RESPONSE } = orderStatus
const { MERIM_TIMEOUT } = customTimeout
const { CLICK_AND_COLLECT, DELIVERY_IN_APP } = ecommerceType
const { DELIVERY_IN_APP: PICK_UP_TYPE_DELIVERY, TABLE_ORDER } = orderPickUpType

type GetState = () => RootState

type ActionSetError = {
    type: 'ECOMM_SET_ERROR',
    error?: {
        message?: string,
        reason?: string,
        customReason?: string
    }
}

type ActionSetHideNeptingCloseButton = {
    type: 'ECOMM_SET_HIDE_NEPTING_CLOSE_BUTTON',
    hideNeptingCloseButton: boolean
}

type ActionSetEcommerceMode = {
    type: 'ECOMM_SET_ECOMMERCE_MODE',
    ecommerceMode: string
}

type ActionSetDeliveryAddress = {
    type: 'ECOMM_SET_DELIVERY_ADDRESS',
    deliveryAddress: string
}

type ActionSetDeliveryInfos = {
    type: 'ECOMM_SET_DELIVERY_INFOS',
    deliveryInfos: {
        infos?: string,
        dropOffType: string
    }
}

type ActionSetDeliveryFees = {
    type: 'ECOMM_SET_DELIVERY_FEES',
    deliveryFees?: DeliveryFees,
    deliveryFeesPurchasable?: PurchasableItem
}

type ActionSetRestaurant = {
    type: 'ECOMM_SET_RESTAURANT',
    restaurant: OrderRestaurantShortDTO
}

type ActionSetCollectMode = {
    type: 'ECOMM_SET_COLLECT_MODE',
    collectMode: string,
    pickUpInfo?: string
}

type ActionUpdateCart = {
    type: 'ECOMM_UPDATE_CART_CONTENT',
    products: Array<PurchasableItem>
}

type ActionAddProductToCart = {
    type: 'ECOMM_ADD_PRODUCT_TO_CART',
    products: Array<PurchasableItem>,
    productAdded: PurchasableItem
}

type ActionRemoveProductFromCart = {
    type: 'ECOMM_REMOVE_PRODUCT_FROM_CART',
    products: Array<PurchasableItem>
}

export type ActionSetUserInfos = {
    type: 'ECOMM_SET_USER_INFOS',
    userInfos: ProfileUserForm
}

type ActionSetCurrentItemInfos = {
    type: 'ECOMM_SET_CURRENT_ITEM_INFOS',
    itemInfos: ItemInfos
}

type ActionSetCurrentItem = {
    type: 'ECOMM_SET_CURRENT_PRODUCT',
    product?: PurchasableItem
}

type ActionResetCurrentItem = {
    type: 'ECOMM_RESET_CURRENT_PRODUCT'
}

type ActionSetCartToast = {
    type: 'ECOMM_SET_CART_TOAST',
    isCartToastVisible: boolean
}

type ActionSetRandomCoupon = {
    type: 'ECOMM_SET_RANDOM_COUPON',
    randomCoupon: {
        showModal: boolean,
        isWinner: boolean,
        imageUrl?: string,
        imageLabel?: string,
        operationRepeatAttributionType?: string
    }
}

type ActionResetCart = {
    type: 'ECOMM_RESET_CART'
}

type ActionResetOrder = {
    type: 'ECOMM_RESET_ORDER'
}

type ActionUpdateOrders = {
    type: 'ECOMM_UPDATE_ORDERS',
    orders: { [string]: ?EcommOrder },
    isDelivery?: boolean
}

type ActionHandleMissingProducts = {
    type: 'ECOMM_HANDLE_MISSING_PRODUCTS',
    idsMissingProducts?: MissingIds,
    products: Array<PurchasableItem>
}

type ActionUpdateCurrentOrder = {
    type: 'ECOMM_UPDATE_CURRENT_ORDER',
    order: EcommOrder
}
type ActionRemoveCurrentOrderToken = {
    type: 'ECOMM_REMOVE_CURRENT_ORDER_TOKEN'
}

type ActionUpdateOrder = {
    type: 'ECOMM_UPDATE_ORDER',
    order: EcommOrder
}

type ActionHasLastOrder = {
    type: 'ECOMM_HAS_LAST_ORDER',
    hasLastOrder: boolean
}

type ActionOrderRejected = {
    type: 'ECOMM_ORDER_REJECTED'
}

type ActionSetPromotion = {
    type: 'ECOMM_SET_PROMOTIONS',
    promotions: Array<ClickAndCollectPromotion>
}

type ActionHandleCoupon = {
    type: 'ECOMM_HANDLE_COUPON',
    coupons: Array<string>
}

type ActionOrderLogout = {
    type: 'LOGOUT'
}

type ActionDiscardOrder = {
    type: 'ECOMM_DISCARD_ORDER',
    id: string,
    pickUpType?: string
}

type ActionSetCurrentSuggestions = {
    type: 'ECOMM_SET_CURRENT_SUGGESTIONS',
    suggestions: Array<PurchasableItem>
}

type ActionPrepareOrder = {
    type: 'ECOMM_PREPARE_ORDER',
    order: EcommOrder
}

type ActionUpdateOrderOrbStatus = {
    type: 'ECOMM_UPDATE_ORDER_ORB_STATUS',
    token: string,
    status: string,
    pickUpType?: string
}

type ActionParkingOpen = {
    type: 'ECOMM_PARKING_OPEN',
    parkingOpen: boolean
}

type ActionFetchPriceEdges = {
    type: 'ECOMM_FETCH_PRICE_EDGES',
    priceEdges: PriceEdgeItem
}

type ActionFetchDrivers = {
    type: 'ECOMM_FETCH_DRIVERS',
    orderJobs: Array<OrderJobDriverResponse>
}

type ActionSetReceiptUrl = {
    type: 'ECOMM_SET_RECEIPT_URL',
    orderId: string,
    receiptUrl: string,
    target: 'deliveryOrders' | 'cacOrders'
}

type ActionAdyenPaymentMethods = {
    type: 'ECOMM_ADYEN_PAYMENT_METHODS',
    paymentMethods: {
        paymentMethods: PaymentMethod[],
        storedPaymentMethods: StoredPaymentMethod[]
    }
}

type ActionAdyenPaymentResponse = {
    type: 'ECOMM_ADYEN_PAYMENT_RESPONSE',
    paymentResponse: PaymentResponse
}

type ActionAdyenRedirection = {
    type: 'ECOMM_ADYEN_SET_REDIRECTION',
    redirection?: {
        orderId?: string,
        orderToken?: string
    }
}

type ActionSetLoader = {
    type: 'ECOMM_SET_LOADER',
    loader: { isLoading: boolean, intlKeyMessage?: string }
}

const actionSetLoader = (loader: {
    isLoading: boolean,
    intlKeyMessage?: string
}): ActionSetLoader => ({
    type: 'ECOMM_SET_LOADER',
    loader
})

const actionSetError = (error?: {
    message?: string,
    reason?: string,
    customReason?: string
}): ActionSetError => ({
    type: 'ECOMM_SET_ERROR',
    error
})

const actionPrepareOrder = (order: EcommOrder): ActionPrepareOrder => ({
    type: 'ECOMM_PREPARE_ORDER',
    order
})

const actionUpdateOrderOrbStatus = (
    token: string,
    status: string,
    pickUpType?: string
): ActionUpdateOrderOrbStatus => ({
    type: 'ECOMM_UPDATE_ORDER_ORB_STATUS',
    token,
    status,
    pickUpType
})

const actionSetHideNeptingCloseButton = (
    hideNeptingCloseButton: boolean
): ActionSetHideNeptingCloseButton => ({
    type: 'ECOMM_SET_HIDE_NEPTING_CLOSE_BUTTON',
    hideNeptingCloseButton
})

const actionSetEcommerceMode = (
    ecommerceMode: string
): ActionSetEcommerceMode => ({
    type: 'ECOMM_SET_ECOMMERCE_MODE',
    ecommerceMode
})

const actionSetDeliveryAddress = (
    deliveryAddress: string
): ActionSetDeliveryAddress => ({
    type: 'ECOMM_SET_DELIVERY_ADDRESS',
    deliveryAddress
})

const actionSetDeliveryInfos = (deliveryInfos: {
    infos?: string,
    dropOffType: string
}): ActionSetDeliveryInfos => ({
    type: 'ECOMM_SET_DELIVERY_INFOS',
    deliveryInfos
})

const actionSetDeliveryFees = (
    deliveryFees?: DeliveryFees,
    deliveryFeesPurchasable?: PurchasableItem
): ActionSetDeliveryFees => ({
    type: 'ECOMM_SET_DELIVERY_FEES',
    deliveryFees,
    deliveryFeesPurchasable
})

const actionSetRestaurant = (
    restaurant: OrderRestaurantShortDTO
): ActionSetRestaurant => ({
    type: 'ECOMM_SET_RESTAURANT',
    restaurant
})

const actionSetCollectMode = (
    collectMode: string,
    pickUpInfo?: string
): ActionSetCollectMode => ({
    type: 'ECOMM_SET_COLLECT_MODE',
    collectMode,
    pickUpInfo
})

const actionParkingOpen = (parkingOpen: boolean): ActionParkingOpen => ({
    type: 'ECOMM_PARKING_OPEN',
    parkingOpen
})

const actionFetchPriceEdges = (
    priceEdges: PriceEdgeItem
): ActionFetchPriceEdges => ({
    type: 'ECOMM_FETCH_PRICE_EDGES',
    priceEdges
})

const actionFetchDrivers = (
    orderJobs: Array<OrderJobDriverResponse>
): ActionFetchDrivers => ({
    type: 'ECOMM_FETCH_DRIVERS',
    orderJobs
})

const setError = (error?: {
    message?: string,
    messageParam?: any,
    reason?: string,
    customReason?: string
}) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(actionSetError(error))
    }
}

const fetchRestaurant = (
    pageUrl: string,
    isDelivery?: boolean,
    noCartReset?: boolean
) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const ecommerceMode = isDelivery
                ? DELIVERY_IN_APP
                : CLICK_AND_COLLECT
            const restaurant = await dispatch(
                fetchRestaurantData(pageUrl, ecommerceMode)
            )
            if (restaurant) {
                if (isEcommerceModeActive(restaurant, ecommerceMode)) {
                    !noCartReset && dispatch(resetCart())
                    dispatch(setRestaurant(restaurant))
                    return restaurant && restaurant.id
                }
                return null
            }
        } catch (error) {
            console.error('Error fetching restaurant', error)
            return null
        }
    }
}

const setHideNeptingCloseButton = (hideButton: boolean) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(actionSetHideNeptingCloseButton(hideButton))
    }
}

const setAdyenRedirection = (redirection?: {
    orderId?: string,
    orderToken?: string
}) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(actionAdyenRedirection(redirection))
    }
}

const setLoader = (loader: { isLoading: boolean, intlKeyMessage?: string }) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(actionSetLoader(loader))
    }
}

const setEcommerceMode = (ecommerceMode: string) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(actionSetEcommerceMode(ecommerceMode))
    }
}

const setDeliveryAddress = (deliveryAddress: string) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(actionSetDeliveryAddress(deliveryAddress))
    }
}

const setDeliveryInfos = (deliveryInfos: {
    infos?: string,
    dropOffType: string
}) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(actionSetDeliveryInfos(deliveryInfos))
    }
}

const deliveryFeesToPurchasable = (
    selectedDeliveryFees: DeliveryFees,
    deliveryFeesProduct: Product,
    finalPrice: number,
    feesPurchasable?: PurchasableItem,
    promotionsById?: MapById<CacPromotion>,
    promotion?: ClickAndCollectPromotion
) => {
    let promotionToApply = promotion
    const { higherPrice, promotionId } = selectedDeliveryFees
    const shouldApplyPromotionFromPreviousDeliveryFees =
        !promotion &&
        feesPurchasable &&
        feesPurchasable.id === deliveryFeesProduct.id &&
        feesPurchasable.promotions &&
        feesPurchasable.promotions.length > 0 &&
        !feesPurchasable.promotions.find(
            promotion =>
                promotion.promotionId === promotionId && !promotion.couponCode
        )
    if (finalPrice >= higherPrice) {
        const deliveryFeesPromotion =
            promotionId && promotionsById && promotionsById[promotionId]
        if (deliveryFeesPromotion) {
            const {
                id,
                idReboot,
                promoType,
                promoValue = 0,
                name,
                threshold
            } = deliveryFeesPromotion
            promotionToApply = {
                promotionId: id,
                idReboot,
                promoType,
                promoValue,
                promotionName: name,
                threshold
            }
        }
    } else if (shouldApplyPromotionFromPreviousDeliveryFees) {
        //$FlowFixMe
        promotionToApply = feesPurchasable.promotions[0]
    }
    return purchasable(deliveryFeesProduct, undefined, promotionToApply)
}

const setDeliveryFees = (promotion?: ClickAndCollectPromotion) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { finalPrice } = priceSelector(getState())
        const { promotionsById } = getState().ecommerceCatalogState
        const {
            restaurant,
            deliveryFees: { feesPurchasable }
        } = getState().ecommerceOrderState
        //$FlowFixMe
        const { deliveryFees } = restaurant || {}
        const selectedDeliveryFees = selectDeliveryFees(
            finalPrice,
            deliveryFees
        )
        let deliveryFeesPurchasable
        if (selectedDeliveryFees) {
            const deliveryFeesProduct = getProductById(
                getState().ecommerceCatalogState,
                selectedDeliveryFees.id
            )
            if (deliveryFeesProduct) {
                deliveryFeesPurchasable = deliveryFeesToPurchasable(
                    selectedDeliveryFees,
                    deliveryFeesProduct,
                    finalPrice,
                    feesPurchasable,
                    promotionsById,
                    promotion
                )
            }
        }
        dispatch(
            actionSetDeliveryFees(
                selectedDeliveryFees,
                deliveryFeesPurchasable || undefined
            )
        )
    }
}

const updateDeliveryRestaurant = () => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const state = getState()
        const { ecommerceMode, restaurant } = state.ecommerceOrderState
        const isDelivery = ecommerceMode === DELIVERY_IN_APP
        if (isDelivery && restaurant) {
            await dispatch(fetchRestaurant(restaurant.id, true, true))
        }
    }
}

const setRestaurant = (restaurant: OrderRestaurantShortDTO) => {
    return async (dispatch: Dispatch<any>) => {
        dispatch(actionSetRestaurant(restaurant))
        dispatch(setDeliveryFees())
    }
}

const setCollectMode = (collectMode: string, pickUpInfo?: string) => {
    return async (dispatch: Dispatch<any>) => {
        if (Object.values(orderPickUpType).includes(collectMode)) {
            if (!!pickUpInfo) {
                dispatch(actionSetCollectMode(collectMode, pickUpInfo))
            } else {
                dispatch(actionSetCollectMode(collectMode))
            }
            return true
        }
        return false
    }
}

const isParkingOpen = (restaurantId?: string) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            let path = endpoints.ecommerce.parkingOpen
            restaurantId && (path += `?frNumber=${restaurantId}`)
            const response = await ws.get(path)
            dispatch(actionParkingOpen(response.data))
        } catch (error) {
            console.log('Error getting parking opening', error)
        }
    }
}

const isTableServiceAvailable = (restaurantId: string) => {
    return async () => {
        try {
            const response = await ws.get(
                endpoints.ecommerce.tableServiceAvailable,
                {
                    params: { frNumber: restaurantId }
                }
            )
            return response.data
        } catch (error) {
            console.error('Error getting table service availability', error)
        }
    }
}

const fetchCartPriceEdges = () => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await ws.get(endpoints.ecommerce.priceEdge)
            dispatch(actionFetchPriceEdges(response.data))
        } catch (error) {
            console.log('Error fetching price edge', error)
        }
    }
}

const setUserInfos = (userInfos: ProfileUserForm): ActionSetUserInfos => ({
    type: 'ECOMM_SET_USER_INFOS',
    userInfos
})

const setCurrentItemInfos = (
    itemInfos: ItemInfos
): ActionSetCurrentItemInfos => ({
    type: 'ECOMM_SET_CURRENT_ITEM_INFOS',
    itemInfos
})

const setCurrentItem = (product: PurchasableItem): ActionSetCurrentItem => ({
    type: 'ECOMM_SET_CURRENT_PRODUCT',
    product
})

const resetCurrentItem = (): ActionResetCurrentItem => ({
    type: 'ECOMM_RESET_CURRENT_PRODUCT'
})

const setCurrentSuggestions = (
    suggestions: Array<PurchasableItem>
): ActionSetCurrentSuggestions => ({
    type: 'ECOMM_SET_CURRENT_SUGGESTIONS',
    suggestions
})

const resetCart = (): ActionResetCart => ({
    type: 'ECOMM_RESET_CART'
})

const resetOrder = (): ActionResetOrder => ({
    type: 'ECOMM_RESET_ORDER'
})

const setRandomCoupon = (randomCoupon: {
    showModal: boolean,
    isWinner: boolean,
    imageLabel?: string,
    imageUrl?: string,
    operationRepeatAttributionType?: string
}): ActionSetRandomCoupon => ({
    type: 'ECOMM_SET_RANDOM_COUPON',
    randomCoupon
})

const setCartToast = (isCartToastVisible: boolean): ActionSetCartToast => ({
    type: 'ECOMM_SET_CART_TOAST',
    isCartToastVisible
})

const actionHandleMissingProducts = (
    products: Array<PurchasableItem>,
    idsMissingProducts?: MissingIds
): ActionHandleMissingProducts => ({
    type: 'ECOMM_HANDLE_MISSING_PRODUCTS',
    products,
    idsMissingProducts
})

const actionSetReceiptUrl = (
    receiptUrl: string,
    orderId: string,
    target: 'deliveryOrders' | 'cacOrders'
): ActionSetReceiptUrl => ({
    type: 'ECOMM_SET_RECEIPT_URL',
    receiptUrl,
    orderId,
    target
})

const discard = (order: EcommOrder) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const { id, pickUpType, token } = order
            if (id && token) {
                await ws.post(
                    `${endpoints.ecommerce.orders}/${id}/discard?token=${token}`
                )
                dispatch(actionDiscard(id, pickUpType))
            }
        } catch (error) {
            if (error.request) {
                throw errors.NETWORK
            }
            throw error
        }
    }
}

const actionDiscard = (
    id: string,
    pickUpType?: string
): ActionDiscardOrder => ({
    type: 'ECOMM_DISCARD_ORDER',
    id,
    pickUpType
})

const actionHandleCoupon = (coupons: Array<string>): ActionHandleCoupon => ({
    type: 'ECOMM_HANDLE_COUPON',
    coupons
})

const actionSetPromotions = (
    promotions: Array<ClickAndCollectPromotion>
): ActionSetPromotion => ({
    type: 'ECOMM_SET_PROMOTIONS',
    promotions
})

const actionUpdateCart = (
    products: Array<PurchasableItem>
): ActionUpdateCart => ({
    type: 'ECOMM_UPDATE_CART_CONTENT',
    products
})

const actionAddProductToCart = (
    products: Array<PurchasableItem>,
    productAdded: PurchasableItem
): ActionAddProductToCart => ({
    type: 'ECOMM_ADD_PRODUCT_TO_CART',
    products,
    productAdded
})
const actionRemoveProductFromCart = (
    products: Array<PurchasableItem>,
    productRemoved: PurchasableItem
): ActionRemoveProductFromCart => ({
    type: 'ECOMM_REMOVE_PRODUCT_FROM_CART',
    products,
    productRemoved
})

const actionUpdateOrders = (
    orders: {
        [string]: ?EcommOrder
    },
    isDelivery?: boolean
): ActionUpdateOrders => ({
    type: 'ECOMM_UPDATE_ORDERS',
    orders,
    isDelivery
})

const actionUpdateCurrentOrder = (
    order: EcommOrder
): ActionUpdateCurrentOrder => ({
    type: 'ECOMM_UPDATE_CURRENT_ORDER',
    order
})

const removeCurrentOrderToken = (): ActionRemoveCurrentOrderToken => ({
    type: 'ECOMM_REMOVE_CURRENT_ORDER_TOKEN'
})

const actionUpdateOrder = (order: EcommOrder): ActionUpdateOrder => ({
    type: 'ECOMM_UPDATE_ORDER',
    order
})

const actionHasLastOrder = (hasLastOrder: boolean): ActionHasLastOrder => ({
    type: 'ECOMM_HAS_LAST_ORDER',
    hasLastOrder
})

const actionOrderRejected = (): ActionOrderRejected => ({
    type: 'ECOMM_ORDER_REJECTED'
})

const actionAdyenPaymentMethods = (
    paymentMethods
): ActionAdyenPaymentMethods => ({
    type: 'ECOMM_ADYEN_PAYMENT_METHODS',
    paymentMethods
})

export const actionAdyenPaymentResponse = (
    paymentResponse: any
): ActionAdyenPaymentResponse => ({
    type: 'ECOMM_ADYEN_PAYMENT_RESPONSE',
    paymentResponse
})

export const actionAdyenRedirection = (redirection?: {
    orderId?: string,
    orderToken?: string
}): ActionAdyenRedirection => ({
    type: 'ECOMM_ADYEN_SET_REDIRECTION',
    redirection
})

const handleMissingProducts = (ecommOrder?: EcommOrder) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        if (!ecommOrder) {
            let products = [...getState().ecommerceOrderState.products]
            products = products.filter(product => product.available === true)
            dispatch(actionHandleMissingProducts(products, undefined))
        } else {
            const { restaurant } = getState().ecommerceOrderState
            if (restaurant) {
                try {
                    await dispatch(fetchCatalog(restaurant.id))
                } catch (error) {
                    console.error('error fetching catalog', error)
                }
            }
            const { missingMenuIds, missingProductIds } = ecommOrder
            const idsMissingProducts = {
                missingMenuIds,
                missingProductIds
            }
            const products = ecommOrderToCart(
                ecommOrder,
                getState().ecommerceCatalogState
            )

            let updatedProducts = products || []

            const updateAvailable = (
                item: PurchasableItem,
                idsMissing?: Array<string>
            ) => {
                if (idsMissing && idsMissing.some(id => id === item.id)) {
                    return { ...item, available: false }
                } else {
                    return item
                }
            }

            if (idsMissingProducts) {
                //$FlowFixMe
                updatedProducts = products.map(product => {
                    if (
                        product.type === catalogType.MENU &&
                        missingMenuIds &&
                        missingMenuIds.length > 0
                    ) {
                        return updateAvailable(product, missingMenuIds)
                    }
                    if (missingProductIds && missingProductIds.length > 0) {
                        if (product.type !== catalogType.MENU) {
                            return updateAvailable(product, missingProductIds)
                        } else if (!!product.steps) {
                            if (
                                product.steps.find(step =>
                                    missingProductIds.some(id => id === step.id)
                                )
                            ) {
                                return { ...product, available: false }
                            } else {
                                return product
                            }
                        } else {
                            return product
                        }
                    }
                    return product
                })
            }

            dispatch(
                actionHandleMissingProducts(updatedProducts, idsMissingProducts)
            )
        }
    }
}

const handlePriceDifference = (ecommOrder: EcommOrder) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { restaurant } = getState().ecommerceOrderState
        if (restaurant) {
            try {
                await dispatch(fetchCatalog(restaurant.id))
            } catch (error) {
                console.error('error fetching catalog', error)
            }
        }
        const updatedCartItems = ecommOrderToCart(
            ecommOrder,
            getState().ecommerceCatalogState
        )
        updatedCartItems && dispatch(actionUpdateCart(updatedCartItems))
    }
}

const handleUnavailableGame = (gameProduct: PurchasableItem) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const {
            products,
            idsCouponsTemporaryBurned
        } = getState().ecommerceOrderState
        const { gamesById } = getState().ecommerceCatalogState
        const updatedProducts = products.map((product: PurchasableItem) => {
            if (
                product.id === gameProduct.id &&
                gamesById &&
                gameProduct.gameId
            ) {
                const game = gamesById[gameProduct.gameId.toString()]
                return {
                    ...gameProduct,
                    available: game?.available,
                    active: game?.active
                }
            }
            return product
        })
        const couponCode =
            gameProduct.promotions && gameProduct.promotions[0].couponCode
        let idsCouponsTemporaryBurnedUpdated = []
        idsCouponsTemporaryBurned.forEach((id: string) => {
            if (id !== couponCode) {
                idsCouponsTemporaryBurnedUpdated.push(id)
            }
        })

        dispatch(actionHandleCoupon(idsCouponsTemporaryBurnedUpdated))
        dispatch(actionUpdateCart(updatedProducts))
    }
}

const addPromotionToCart = (promotion: ClickAndCollectPromotion) => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        const promotions = [...getState().ecommerceOrderState.promotions]
        const idsCouponsTemporaryBurned = [
            ...getState().ecommerceOrderState.idsCouponsTemporaryBurned
        ]
        promotions.push(promotion)
        if (promotion.couponCode) {
            idsCouponsTemporaryBurned.push(promotion.couponCode)
            dispatch(actionHandleCoupon(idsCouponsTemporaryBurned))
        }
        dispatch(actionSetPromotions(promotions))
        dispatch(setDeliveryFees())
    }
}

const removePromotionFromCart = (couponCode: string) => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        const promotions = [...getState().ecommerceOrderState.promotions]
        const idsCouponsTemporaryBurned = [
            ...getState().ecommerceOrderState.idsCouponsTemporaryBurned
        ]

        const indexPromo = promotions.findIndex(
            promo => promo.couponCode === couponCode
        )
        if (indexPromo >= 0) {
            promotions.splice(indexPromo, 1)
        }
        const indexCoupons = idsCouponsTemporaryBurned.findIndex(
            idCoupon => idCoupon === couponCode
        )
        if (indexCoupons >= 0) {
            idsCouponsTemporaryBurned.splice(indexCoupons, 1)
        }
        dispatch(cancelCoupon(couponCode))
        dispatch(actionSetPromotions(promotions))
        dispatch(actionHandleCoupon(idsCouponsTemporaryBurned))
        dispatch(setDeliveryFees())
    }
}

const addProductToCart = (product: PurchasableItem) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const products = [...getState().ecommerceOrderState.products]
        const { restaurant, collectMode, randomCoupon } =
            getState().ecommerceOrderState || {}
        const idsCouponsTemporaryBurned = [
            ...getState().ecommerceOrderState.idsCouponsTemporaryBurned
        ]
        const { operationsRepeat } = getState().ecommerceCatalogState
        const index = findProductInCart(products, product)
        let couponCode
        if (index >= 0) {
            if (product.type === catalogType.GAME) {
                const currentOperationRepeat =
                    operationsRepeat &&
                    operationsRepeat.find(o => o.code === product.operationCode)
                const coupon =
                    currentOperationRepeat &&
                    currentOperationRepeat.coupons &&
                    currentOperationRepeat.coupons.length > 0 &&
                    currentOperationRepeat.coupons.find(
                        c =>
                            !idsCouponsTemporaryBurned.includes(
                                c.restaurantCode
                            )
                    )

                if (coupon && products[index].promotions) {
                    products[index].promotions.push({
                        couponCode: coupon.restaurantCode
                    })
                    idsCouponsTemporaryBurned.push(coupon.restaurantCode)
                    dispatch(actionHandleCoupon(idsCouponsTemporaryBurned))
                    couponCode = coupon.restaurantCode
                    products[index].quantity += product.quantity || 1
                }
            } else {
                products[index].quantity += product.quantity || 1
            }
        } else {
            products.push(product)
            if (
                product.promotions &&
                product.promotions.length > 0 &&
                product.promotions[0].couponCode
            ) {
                idsCouponsTemporaryBurned.push(product.promotions[0].couponCode)
                //$FlowFixMe
                couponCode = product.promotions[0].couponCode

                dispatch(actionHandleCoupon(idsCouponsTemporaryBurned))
            }

            if (
                randomCoupon &&
                randomCoupon.operationRepeatAttributionType ===
                    OPERATION_REPEAT_ATTRIBUTION_TYPE.RANDOM
            ) {
                dispatch(setRandomCoupon({ ...randomCoupon, showModal: true }))
            } else {
                dispatch(setCartToast(true))
            }
        }

        if (couponCode && restaurant && restaurant.id) {
            // Add a security check for backend BE-7749
            try {
                await ws.post(endpoints.ecommerce.addCoupon, {
                    code: couponCode,
                    restaurant: restaurant.id,
                    pickUpType: collectMode
                })
            } catch (e) {
                const message = handleCouponMessageError(e)
                dispatch(setError({ message }))
                return
            }
        }
        dispatch(actionAddProductToCart(products, product))
        dispatch(setDeliveryFees())
    }
}

const addMultipleProductsToCart = (products: Array<PurchasableItem>) => {
    return (dispatch: Dispatch<any>) => {
        products.forEach(product => {
            dispatch(addProductToCart(product))
        })
    }
}

const fillCartWithMultipleProducts = (products?: Array<PurchasableItem>) => {
    return (dispatch: Dispatch<any>) => {
        if (products && products.length > 0) {
            dispatch(actionUpdateCart(products))
        }
    }
}

const removeProductFromCart = (product: PurchasableItem) => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        let products = [...getState().ecommerceOrderState.products]
        const idsCouponsTemporaryBurned = [
            ...getState().ecommerceOrderState.idsCouponsTemporaryBurned
        ]
        let couponCode = null
        const index = findProductInCart(products, product)
        if (index >= 0) {
            if (products[index].quantity > 1) {
                products[index].quantity -= 1
                if (
                    product.type === catalogType.GAME &&
                    products[index].promotions
                ) {
                    const coupon = products[index].promotions.pop()
                    couponCode = coupon
                    const indexCoupon = idsCouponsTemporaryBurned.findIndex(
                        e => e === coupon.couponCode
                    )
                    if (coupon && indexCoupon >= 0) {
                        idsCouponsTemporaryBurned.splice(indexCoupon, 1)
                        dispatch(actionHandleCoupon(idsCouponsTemporaryBurned))
                    }
                }
            } else {
                if (product.promotions && product.promotions.length > 0) {
                    couponCode = product.promotions[0].couponCode
                    if (couponCode && isKingdomPointsCoupon(couponCode)) {
                        dispatch(cancelCoupon(couponCode))
                    } else {
                        idsCouponsTemporaryBurned.forEach(id => {
                            if (id === couponCode) {
                                dispatch(cancelCoupon(id))
                            }
                        })
                    }
                    const index = idsCouponsTemporaryBurned.findIndex(
                        id =>
                            product.promotions &&
                            product.promotions.length > 0 &&
                            id === couponCode
                    )

                    if (index >= 0) {
                        couponCode = idsCouponsTemporaryBurned[index]
                        idsCouponsTemporaryBurned.splice(index, 1)
                    }
                    dispatch(actionHandleCoupon(idsCouponsTemporaryBurned))
                }
                products.splice(index, 1)
            }
            const linkedProduct = findLinkedProduct(products, product)
            if (linkedProduct) {
                products = products.filter(prod => prod.id !== linkedProduct.id)
                product = linkedProduct
            }
            if (couponCode) {
                //$FlowFixMe
                cancelCoupon(couponCode)
            }
            dispatch(actionRemoveProductFromCart(products, product))
            dispatch(setDeliveryFees())
        }
    }
}

const updateProductInCart = (
    previousProduct: PurchasableItem,
    product: PurchasableItem
) => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        const products = [...getState().ecommerceOrderState.products]
        const index = findProductInCart(products, previousProduct)
        if (index >= 0) {
            products[index] = product
        } else {
            products.push(product)
        }
        dispatch(actionUpdateCart(products))
        dispatch(setDeliveryFees())
    }
}

let preparing = new Set() // used to prevent multiple prepareOrder calls for the same order
// throws:
// CC_ORDER_NOT_FOUND, REJECTED, CLIENT, SERVER, NETWORK, OTHER, MISSING_PRODUCTS, INVALID_RESPONSE, RESTAURANT_CLOSED, REJECTED
const prepareOrder = (orderPrepare: SendOrderPrepare) => {
    const { token, pickUpType, pickUpInfo } = orderPrepare

    return async (dispatch: Dispatch<any>, getState: GetState) => {
        if (preparing.has(token)) {
            return
        }
        preparing.add(token)
        const {
            cacOrders,
            currentToken,
            deliveryOrders
        } = getState().ecommerceOrderState
        const orderToken = token ? token : currentToken

        const currentOrder = getCurrentOrder(
            cacOrders,
            deliveryOrders,
            orderToken
        )
        if (!currentOrder) {
            throw errors.ECOMM_ORDER_NOT_FOUND
        }
        orderToken &&
            dispatch(
                actionUpdateOrderOrbStatus(
                    orderToken,
                    WAITING_RESPONSE,
                    pickUpType
                )
            )
        const { id, pickUpType: currentOrderPickUpType } = currentOrder

        let response = null
        const isDelivery = currentOrderPickUpType === PICK_UP_TYPE_DELIVERY
        try {
            const client = isDelivery ? deliveryClient : ws
            response =
                id &&
                orderToken &&
                (await client.post(
                    `${endpoints.ecommerce.orders}/${id}/preparation?token=${orderToken}`,
                    {
                        'pick-up-type': pickUpType,
                        'pick-up-info': pickUpInfo
                    },
                    { timeout: MERIM_TIMEOUT }
                ))
            dispatch(actionPrepareOrder(currentOrder))
        } catch (error) {
            const { request, response } = error
            orderToken &&
                dispatch(
                    actionUpdateOrderOrbStatus(orderToken, ACCEPTED, pickUpType)
                )
            if (response.data) {
                const { message } = response.data
                switch (message) {
                    case errors.PARKING_CLOSED:
                        await dispatch(actionParkingOpen(false))
                        throw errors.PARKING_CLOSED
                    case errors.CAC_PREPARATION_CLOSED:
                        throw errors.CAC_PREPARATION_CLOSED
                    case errors.FEATURE_NOT_AVAILABLE:
                        throw errors.FEATURE_NOT_AVAILABLE
                    case errors.STUART_FAILURE:
                    case errors.DELIVERY_PROVIDER_FAILURE:
                        orderToken &&
                            dispatch(
                                actionUpdateOrder({
                                    ...deliveryOrders[orderToken],
                                    orderStatus: ecommOrderStatus.DELIVERY_ERROR
                                })
                            )
                        throw errors.DELIVERY_PROVIDER_FAILURE
                    default:
                        break
                }
            }
            // Technical errors
            if (response && response.status < 500) {
                throw errors.CLIENT
            }
            if (response && response.status >= 500) {
                isDelivery &&
                    orderToken &&
                    dispatch(
                        actionUpdateOrder({
                            ...deliveryOrders[orderToken],
                            orderStatus: ecommOrderStatus.SYSTEM_CANCELLED
                        })
                    )
                throw errors.SERVER
            }
            if (request) {
                throw errors.NETWORK
            }
            throw errors.OTHER
        } finally {
            preparing.delete(token)
        }

        // Functional errors
        if (!response || !response.data) {
            orderToken &&
                dispatch(
                    actionUpdateOrderOrbStatus(orderToken, ACCEPTED, pickUpType)
                )
            throw errors.INVALID_RESPONSE
        }
        dispatch(actionUpdateOrder({ ...response.data, token: orderToken }))

        const { missingMenuIds, missingProductIds, orderStatus } = response.data
        if (
            (missingMenuIds && missingMenuIds.length > 0) ||
            (missingProductIds && missingProductIds.length > 0)
        ) {
            dispatch(handleMissingProducts(response.data))
            throw errors.MISSING_PRODUCTS
        }

        if (
            orderStatus === ecommOrderStatus.SYSTEM_CANCELLED ||
            orderStatus === ecommOrderStatus.REJECTED
        ) {
            throw errors.REJECTED
        }
    }
}

const reportInvalidOrder = (
    restaurantId: string,
    collectMode: string,
    products: Array<PurchasableItem>
) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { catalog } = getState().ecommerceCatalogState
        try {
            const data = new FormData()
            const catalogAndContent = { catalog, content: products }
            data.append('frNumber', restaurantId)
            data.append('pickUpType', collectMode)
            data.append(
                'catalog',
                new Blob([JSON.stringify(catalogAndContent)], {
                    type: 'application/json'
                }),
                'catalog.json'
            )
            const options = {
                method: 'POST',
                url: endpoints.ecommerce.errors,
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                timeout: 60000,
                data
            }
            await ws(options)
        } catch (error) {
            console.error('Error reporting invalid order content', error)
        }
    }
}

// throws:
// CC_USER_INFOS_NOT_FOUND, CC_RESTAURANT_NOT_FOUND, REJECTED, CLIENT, SERVER, NETWORK, OTHER, INVALID_RESPONSE, PRICE_DIFFERENCE
const sendCurrentOrder = (data: OrderInfos) => {
    return async (
        dispatch: Dispatch<any>,
        getState: GetState
    ): Promise<?{ token?: string, orderId?: string }> => {
        const state = getState()
        const { deviceCheckPayload, userInfos, deliveryAddress } = data
        const {
            currentToken,
            products,
            cacOrders,
            deliveryOrders,
            collectMode,
            promotions,
            restaurant,
            ecommerceMode,
            deliveryFees,
            pickUpInfo = userInfos.pickUpInfo
        } = state.ecommerceOrderState
        const isDelivery = ecommerceMode === DELIVERY_IN_APP
        const isClickAndCollect = ecommerceMode === CLICK_AND_COLLECT
        const isTableOrder = ecommerceMode === TABLE_ORDER
        const { fees, feesPurchasable } = deliveryFees

        const { analyticsData, authenticated } = state.userState

        if (!userInfos) {
            throw errors.ECOMM_USER_INFOS_NOT_FOUND
        }
        dispatch(setUserInfos(userInfos))

        if (
            !restaurant ||
            (isClickAndCollect && !collectMode) ||
            (isDelivery && !deliveryAddress)
        ) {
            throw errors.ECOMM_RESTAURANT_NOT_FOUND
        }

        try {
            await dispatch(fetchCatalog(restaurant.id))
        } catch (error) {
            console.error(
                'Error fetching catalog before sending current order',
                error
            )
        }
        const { gamesById } = getState().ecommerceCatalogState

        const content = products.map((product: PurchasableItem) => {
            if (
                product.type === catalogType.GAME &&
                gamesById &&
                product.gameId
            ) {
                const game = gamesById[product.gameId.toString()]
                if (!game?.active || !game?.available) {
                    dispatch(handleUnavailableGame(product))
                    throw errors.UNAVAILABLE_GAME
                }
            }
            return purchasableToCacOrderContent(product)
        })

        let wsOperation
        let wsEndpoint

        const currentOrder = getCurrentOrder(
            cacOrders,
            deliveryOrders,
            currentToken
        )
        const currentId = currentOrder && currentOrder.id
        const {
            originalPrice: priceOriginal,
            finalPrice: priceTotal
        } = priceSelector(state)
        const orderPrice = getOrderPrice(state, ecommerceMode)
        let wsPayload = {
            deviceCheckPayload,
            workflowNotConnected: !authenticated,
            order: {
                clientId: analyticsData && analyticsData.loyaltyCardCode,
                originalPrice: priceOriginal,
                finalPrice: orderPrice,
                earnedPoints: pointsSelector(state),
                pickUpType: collectMode,
                frNumber: restaurant && restaurant.id,
                clientFirstname: userInfos.firstname,
                clientEmail: userInfos.email,
                clientLastname: userInfos.lastname,
                clientTelephone: userInfos.phone,
                content,
                token: currentToken,
                id: currentId,
                promotions,
                pickUpInfo
            }
        }
        if (isDelivery) {
            let deliveryFees
            if (
                fees &&
                feesPurchasable &&
                ((priceTotal >= fees.higherPrice && !!fees.promotionId) ||
                    priceTotal <= fees.higherPrice)
            ) {
                deliveryFees = purchasableToCacOrderContent(feesPurchasable)
            }
            const coordinates = await getCoordinatesFromWoosmap(
                deliveryAddress?.address
            )

            wsPayload = {
                ...wsPayload,
                order: {
                    ...wsPayload.order,
                    pickUpType: PICK_UP_TYPE_DELIVERY,
                    deliveryAddress: {
                        ...deliveryAddress,
                        coordinates: {
                            longitude: coordinates?.lng,
                            latitude: coordinates?.lat
                        }
                    },
                    deliveryFees: deliveryFees && [deliveryFees]
                }
            }
        }

        // PATCH
        const { BUILT, MODIFIED } = ecommOrderStatus
        const patchStatuses = [BUILT, MODIFIED]
        const enrolment = userInfos.licensePlateEnrolment ? '1' : '0'
        const client = getEcommerceAxiosClient(ecommerceMode)

        if (
            currentToken &&
            currentId &&
            currentOrder &&
            patchStatuses.includes(currentOrder.orderStatus)
        ) {
            wsOperation = client.patch
            wsEndpoint = `${endpoints.ecommerce.orders}/${currentId}?token=${currentToken}&licensePlateEnrolment=${enrolment}`

            // POST
        } else {
            wsOperation = client.post
            if (
                currentId &&
                currentToken &&
                cacOrders[currentToken] &&
                cacOrders[currentToken].orderStatus === 'PAYMENT_PENDING'
            ) {
                //before creating the new order the previous created order will be canceled - will never be used again
                wsEndpoint = `${endpoints.ecommerce.orders}?id=${currentId}&token=${currentToken}&licensePlateEnrolment=${enrolment}`
            } else {
                wsEndpoint = `${endpoints.ecommerce.orders}?licensePlateEnrolment=${enrolment}`
            }
        }

        let response = null
        try {
            response = await wsOperation(wsEndpoint, wsPayload)
            // Technical errors
        } catch (error) {
            const { request, response } = error
            // Rejected
            if (response && response.status === 403) {
                dispatch(actionOrderRejected())
                if (response.data) {
                    switch (response.data.message) {
                        case errors.FEATURE_NOT_AVAILABLE:
                            throw errors.FEATURE_NOT_AVAILABLE
                        case errors.PROMOTION_NOT_VALID:
                            throw errors.PROMOTION_NOT_VALID
                        case errors.LEAP_YEAR_OPERATION_TOTAL_LIMIT_REACHED:
                            throw errors.LEAP_YEAR_OPERATION_TOTAL_LIMIT_REACHED
                        case errors.ABUSIVE_ORDER:
                            throw errors.ABUSIVE_ORDER
                        case errors.TABLE_ORDER_BAD_TOKEN:
                            throw errors.TABLE_ORDER_BAD_TOKEN
                        default:
                            throw errors.REJECTED
                    }
                }
            }
            if (response && response.status < 500) {
                throw errors.CLIENT
            }
            if (response && response.status >= 500) {
                throw errors.SERVER
            }
            if (request) {
                throw errors.NETWORK
            }
            throw errors.OTHER
        }

        if (!response || !response.data) {
            throw errors.INVALID_RESPONSE
        }

        dispatch(actionUpdateCurrentOrder(response.data))

        // Functional errors
        const {
            finalPrice,
            orderStatus,
            missingMenuIds,
            missingProductIds
        } = response.data

        if (
            orderStatus === ecommOrderStatus.MISSING_PRODUCT ||
            (missingMenuIds && missingMenuIds.length > 0) ||
            (missingProductIds && missingProductIds.length > 0)
        ) {
            dispatch(handleMissingProducts(response.data))
            throw errors.MISSING_PRODUCTS
        }

        if (finalPrice !== orderPrice) {
            dispatch(handlePriceDifference(response.data))
            throw errors.PRICE_DIFFERENCE
        }
        const updatedState = getState()
        const updatedCurrentOrder: any = getCurrentOrder(
            updatedState.ecommerceOrderState.cacOrders,
            updatedState.ecommerceOrderState.deliveryOrders,
            updatedState.ecommerceOrderState.currentToken
        )

        if (authenticated && finalPrice > 0) {
            // Payment url will be determined in another process
            return {
                token: updatedCurrentOrder?.token,
                orderId: updatedCurrentOrder?.id
            }
        }

        // Skip payment for free orders
        if (orderPrice === 0) {
            await dispatch(getPaymentUrl(undefined, false))
            dispatch(resetCart())
            if (isDelivery) {
                try {
                    await dispatch(prepareOrder({ token: currentToken }))
                } catch (error) {
                    throw error
                }
            } else if (isTableOrder) {
                try {
                    dispatch(
                        prepareOrder({
                            token:
                                currentToken ??
                                updatedState.ecommerceOrderState.currentToken,
                            pickUpType: TABLE_ORDER,
                            pickUpInfo
                        })
                    )
                } catch (error) {
                    throw error
                }
            }
        }
        return {
            token: updatedCurrentOrder?.token,
            orderId: updatedCurrentOrder?.id
        }
    }
}

// throws:
// CC_ORDER_NOT_FOUND, REJECTED, CLIENT, SERVER, NETWORK, OTHER, MISSING_PRODUCTS, PRICE_DIFFERENCE, INVALID_RESPONSE
const getPaymentUrl = (
    selectedCardId?: string,
    cardEnrolment: boolean = false
) => {
    return async (
        dispatch: Dispatch<any>,
        getState: GetState
    ): Promise<String> => {
        const {
            currentToken,
            cacOrders,
            deliveryOrders,
            ecommerceMode,
            restaurant,
            collectMode,
            products
        } = getState().ecommerceOrderState

        const currentOrder = getCurrentOrder(
            cacOrders,
            deliveryOrders,
            currentToken
        )
        if (!currentOrder) {
            throw errors.ECOMM_ORDER_NOT_FOUND
        }
        const { id, pickUpType } = currentOrder

        const isDelivery = pickUpType === PICK_UP_TYPE_DELIVERY
        const orderPrice = getOrderPrice(
            getState(),
            isDelivery ? DELIVERY_IN_APP : CLICK_AND_COLLECT
        )

        const enrolment = cardEnrolment ? '1' : '0'
        const client = getEcommerceAxiosClient(ecommerceMode)

        let response = null
        try {
            let url = `${endpoints.ecommerce.orders}/${id ||
                ''}/payment?&token=${currentToken ||
                ''}&cardEnrolment=${enrolment}`

            if (selectedCardId) {
                url += `&cbid=${selectedCardId}`
            }

            response =
                id &&
                currentToken &&
                (await client.post(url, {}, { timeout: MERIM_TIMEOUT }))

            // Technical errors
        } catch (error) {
            const { request, response } = error

            // Rejected
            if (response && response.status === 403) {
                dispatch(actionOrderRejected())
                if (response.data) {
                    switch (response.data.message) {
                        case errors.FEATURE_NOT_AVAILABLE:
                            throw errors.FEATURE_NOT_AVAILABLE
                        case errors.ABUSIVE_ORDER:
                            throw errors.ABUSIVE_ORDER
                        case errors.INVALID_CONTENT:
                            if (restaurant && restaurant.id && collectMode) {
                                dispatch(
                                    reportInvalidOrder(
                                        restaurant.id,
                                        collectMode,
                                        products
                                    )
                                )
                            }
                            throw errors.REJECTED
                        default:
                            throw errors.REJECTED
                    }
                }
            }
            if (response && response.status < 500) {
                throw errors.CLIENT
            }
            if (response && response.status >= 500) {
                throw errors.SERVER
            }
            if (request) {
                throw errors.NETWORK
            }
            throw errors.OTHER
        }

        // Functional errors
        if (!response || !response.data) {
            throw errors.INVALID_RESPONSE
        }

        dispatch(actionUpdateCurrentOrder(response.data))

        const {
            finalPrice,
            paymentUrl,
            missingMenuIds,
            missingProductIds,
            orderStatus
        } = response.data

        if (
            orderStatus === ecommOrderStatus.MISSING_PRODUCT ||
            (missingMenuIds && missingMenuIds.length > 0) ||
            (missingProductIds && missingProductIds.length > 0)
        ) {
            dispatch(handleMissingProducts(response.data))
            throw errors.MISSING_PRODUCTS
        }

        if (finalPrice !== orderPrice) {
            dispatch(handlePriceDifference(response.data))
            throw errors.PRICE_DIFFERENCE
        }

        if (!paymentUrl && finalPrice > 0) {
            throw errors.INVALID_RESPONSE
        }

        return paymentUrl
    }
}

// throws:
// CC_ORDER_NOT_FOUND, REJECTED, CLIENT, SERVER, NETWORK, OTHER, MISSING_PRODUCTS, PRICE_DIFFERENCE, INVALID_RESPONSE, PAYMENT_CANCELLED, PAYMENT_ERROR, PAYMENT_REFUSED
const oneClickPayment = (cbid: string) => {
    return async (
        dispatch: Dispatch<any>,
        getState: GetState
    ): Promise<void> => {
        const {
            currentToken,
            cacOrders,
            deliveryOrders,
            restaurant,
            collectMode,
            products,
            ecommerceMode
        } = getState().ecommerceOrderState

        const currentOrder = getCurrentOrder(
            cacOrders,
            deliveryOrders,
            currentToken
        )
        if (!currentOrder) {
            throw errors.ECOMM_ORDER_NOT_FOUND
        }
        const { id, pickUpType, pickUpInfo } = currentOrder

        const isDelivery = pickUpType === PICK_UP_TYPE_DELIVERY
        const isTableOrder = pickUpType === TABLE_ORDER
        const orderPrice = getOrderPrice(getState(), ecommerceMode)

        let response = null
        const client = getEcommerceAxiosClient(ecommerceMode)
        try {
            response =
                id &&
                currentToken &&
                cbid &&
                (await client.post(
                    `${endpoints.ecommerce.orders}/${id}/one-click-payment?token=${currentToken}&cbid=${cbid}`,
                    {},
                    { timeout: MERIM_TIMEOUT }
                ))
        } catch (error) {
            const { request, response } = error
            // Rejected
            if (response && response.status === 403) {
                dispatch(actionOrderRejected())
                if (response.data) {
                    switch (response.data.message) {
                        case errors.FEATURE_NOT_AVAILABLE:
                            throw errors.FEATURE_NOT_AVAILABLE
                        case errors.ABUSIVE_ORDER:
                            throw errors.ABUSIVE_ORDER
                        case errors.INVALID_CONTENT:
                            if (restaurant && restaurant.id && collectMode) {
                                dispatch(
                                    reportInvalidOrder(
                                        restaurant.id,
                                        collectMode,
                                        products
                                    )
                                )
                            }
                            throw errors.REJECTED
                        default:
                            throw errors.REJECTED
                    }
                }
            }
            if (response && response.status < 500) {
                throw errors.CLIENT
            }
            if (response && response.status >= 500) {
                throw errors.SERVER
            }
            if (request) {
                throw errors.NETWORK
            }
            throw errors.OTHER
        }

        if (!response || !response.data) {
            throw errors.INVALID_RESPONSE
        }

        const {
            finalPrice,
            orderStatus,
            missingMenuIds,
            missingProductIds
        } = response.data

        dispatch(actionUpdateCurrentOrder(response.data))

        if (orderStatus !== ecommOrderStatus.PAID) {
            switch (orderStatus) {
                case ecommOrderStatus.PAYMENT_CANCELLED:
                    throw errors.PAYMENT_CANCELLED
                case ecommOrderStatus.PAYMENT_ERROR:
                    throw errors.PAYMENT_ERROR
                case ecommOrderStatus.PAYMENT_REFUSED:
                    throw errors.PAYMENT_REFUSED
                case ecommOrderStatus.MISSING_PRODUCT:
                    if (
                        (missingMenuIds && missingMenuIds.length > 0) ||
                        (missingProductIds && missingProductIds.length > 0)
                    ) {
                        dispatch(handleMissingProducts(response.data))
                    }
                    throw errors.MISSING_PRODUCTS
                case ecommOrderStatus.REJECTED:
                    throw errors.REJECTED
                default:
                    throw errors.OTHER
            }
        }

        if (finalPrice !== orderPrice) {
            dispatch(handlePriceDifference(response.data))
            throw errors.PRICE_DIFFERENCE
        }

        dispatch(resetCart())

        if (isDelivery) {
            try {
                await dispatch(prepareOrder({ token: currentToken }))
            } catch (error) {
                throw error
            }
        }

        if (isTableOrder) {
            try {
                dispatch(
                    prepareOrder({
                        token: currentToken,
                        pickUpType: TABLE_ORDER,
                        pickUpInfo
                    })
                )
            } catch (error) {
                throw error
            }
        }
    }
}

const fetchOrder = (id: string, token: string) => async (
    dispatch: Dispatch<any>,
    getState: GetState
): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        if (!id || !token) {
            reject(errors.ECOMM_ORDER_NOT_FOUND)
            return
        }
        const state = getState()
        const { cacOrders, deliveryOrders } = state.ecommerceOrderState

        const orders = { ...cacOrders, ...deliveryOrders }

        const isDeliveryOrder =
            orders[token] && orders[token].pickUpType === PICK_UP_TYPE_DELIVERY

        const clearAndResolve = timer => {
            clearInterval(timer)
            timer = undefined
            resolve(true)
        }

        const clearAndReject = timer => error => {
            clearInterval(timer)
            timer = undefined
            reject(error)
        }

        let pending = false
        let networkErrors = 0

        const apiUrl = isDeliveryOrder ? deliveryClient : ws

        const timer = setInterval(async () => {
            if (pending) {
                return
            }
            pending = true
            try {
                const response = await apiUrl.get(
                    `${endpoints.ecommerce.orders}/${id}?token=${token}`
                )
                const remoteOrder: EcommOrder = response.data
                dispatch(actionUpdateCurrentOrder(remoteOrder))
            } catch (error) {
                if (++networkErrors > 10) {
                    clearAndReject(timer)(errors.PAYMENT_ERROR)
                }
            } finally {
                clearAndResolve(timer)
                pending = false
            }
        }, 2000)

        // Stop fetch loop, if still running after 30s,
        setTimeout(() => {
            if (timer) {
                clearAndReject(timer)(errors.PAYMENT_ERROR)
            }
        }, 30 * 1000)
    })
}

//Fetch all Click & Collect and delivery orders
const fetchOrders = (fetchOrdersParams?: FetchOrdersParams) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        // fetchAllOrders -> get all the user's orders no matter how old they are
        // fetchDeliveryOrders -> get the user's delivery orders
        const { days, fetchAllOrders, fetchDeliveryOrders } =
            fetchOrdersParams || {}
        const { authenticated } = getState().userState
        let updatedCacOrders = {}
        let updatedDeliveryOrders = {}
        if (authenticated) {
            ws.get(endpoints.ecommerce.orders, {
                params: { days, isLimited: !fetchAllOrders }
            })
                .then(cacResponse => {
                    if (cacResponse && cacResponse.data) {
                        updatedCacOrders = cacResponse.data.reduce(
                            (acc, order) => ({ ...acc, [order.token]: order }),
                            {}
                        )
                    }

                    dispatch(actionUpdateOrders(updatedCacOrders))
                })
                .catch(error => console.error('Error fetching orders', error))
            if (fetchDeliveryOrders) {
                deliveryClient
                    .get(endpoints.ecommerce.orders, {
                        params: { days, isLimited: !fetchAllOrders }
                    })
                    .then(deliveryResponse => {
                        if (deliveryResponse && deliveryResponse.data) {
                            updatedDeliveryOrders = deliveryResponse.data.reduce(
                                (acc, order) => ({
                                    ...acc,
                                    [order.token]: order
                                }),
                                {}
                            )
                        }
                        dispatch(
                            actionUpdateOrders(updatedDeliveryOrders, true)
                        )
                    })
                    .catch(error =>
                        console.error('Error fetching delivery orders', error)
                    )
            }
        }
    }
}

//Fetch last Click & Collect order
const fetchLastCcOrder = () => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const { token } = getState().userState
            const response = await ws.get(endpoints.ecommerce.lastOrder, {
                headers: token ? { RenewToken: true, Authorization: token } : {}
            })
            if (response && response.data) {
                dispatch(actionUpdateOrder(response.data))
                dispatch(actionHasLastOrder(true))
                return response.data
            } else {
                dispatch(actionHasLastOrder(false))
            }
        } catch (error) {
            const { response } = error
            console.error('Error fetching last order', response)
        }
    }
}

// throws:
// CC_ORDER_NOT_FOUND, PAYMENT_CANCELLED, PAYMENT_ERROR, PAYMENT_REFUSED
const waitForOrderStatusChange = (token?: string) => async (
    dispatch: Dispatch<any>,
    getState: GetState
): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        const {
            currentToken = '',
            cacOrders,
            deliveryOrders
        } = getState().ecommerceOrderState

        const tokenToUse = token || currentToken

        const localOrder = getCurrentOrder(
            cacOrders,
            deliveryOrders,
            tokenToUse
        )
        if (!localOrder || !localOrder.id) {
            reject(errors.ECOMM_ORDER_NOT_FOUND)
            return
        }

        const { id, pickUpType, pickUpInfo } = localOrder
        const isDelivery = pickUpType === PICK_UP_TYPE_DELIVERY
        const isTableOrder = pickUpType === TABLE_ORDER

        const clearAndResolve = timer => {
            clearInterval(timer)
            timer = undefined
            resolve(true)
        }

        const clearAndReject = timer => error => {
            clearInterval(timer)
            timer = undefined
            reject(error)
        }

        let pending = false
        let networkErrors = 0

        const timer = setInterval(async () => {
            if (pending) {
                return
            }
            pending = true
            try {
                const client = isDelivery ? deliveryClient : ws
                const response = await client.get(
                    `${endpoints.ecommerce.orders}/${id}?token=${tokenToUse}`
                )
                const remoteOrder: EcommOrder = response.data
                dispatch(actionUpdateCurrentOrder(remoteOrder))
                const {
                    orderStatus = ecommOrderStatus.PAYMENT_PENDING
                } = remoteOrder

                if (isDeliveryOrderPaid(orderStatus)) {
                    dispatch(resetCart())
                    if (orderStatus === ecommOrderStatus.PAID) {
                        if (isDelivery) {
                            try {
                                dispatch(
                                    prepareOrder({
                                        token: tokenToUse,
                                        pickUpType: 'DELIVERY_IN_APP'
                                    })
                                )
                            } catch (error) {
                                clearAndReject(timer)(error)
                            }
                        }
                        if (isTableOrder) {
                            try {
                                dispatch(
                                    prepareOrder({
                                        token: tokenToUse,
                                        pickUpType: TABLE_ORDER,
                                        pickUpInfo
                                    })
                                )
                            } catch (error) {
                                clearAndReject(timer)(error)
                            }
                        }
                    }
                    clearAndResolve(timer)
                } else {
                    switch (orderStatus) {
                        case ecommOrderStatus.PAYMENT_CANCELLED:
                            clearAndReject(timer)(errors.PAYMENT_CANCELLED)
                            break
                        case ecommOrderStatus.PAYMENT_ERROR:
                            clearAndReject(timer)(errors.PAYMENT_ERROR)
                            break
                        case ecommOrderStatus.PAYMENT_REFUSED:
                            clearAndReject(timer)(errors.PAYMENT_REFUSED)
                            break
                        default:
                            // do nothing
                            break
                    }
                }
            } catch (error) {
                if (++networkErrors > 10) {
                    clearAndReject(timer)(errors.PAYMENT_ERROR)
                }
            } finally {
                pending = false
            }
        }, 2000)

        // Stop fetch loop, if still running after 30s,
        setTimeout(() => {
            if (timer) {
                clearAndReject(timer)(errors.PAYMENT_ERROR)
            }
        }, 30 * 1000)
    })
}

const processCouponNavigation = (
    coupon: Coupon | GameCoupon,
    promotion: CacPromotion,
    fromNextPromotionScreen: boolean = false
): ProcessCoupon => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const {
            deliveryFees: { fees },
            promotions,
            products
        } = getState().ecommerceOrderState
        const { gamesById, operationsRepeat } = getState().ecommerceCatalogState
        const {
            promoType,
            menuIds,
            requiredMenuIds,
            productIds,
            requiredProductIds
        } = promotion

        let error,
            listItems,
            type,
            hasOptions,
            isDeliveryFeesPromotion,
            requiredType,
            listRequiredItems

        const promotionOrigin =
            coupon.origin === KINGDOM_POINTS
                ? undefined
                : coupon.origin === OPERATION_REPEAT_COUPON
                ? promotionOriginType.OPERATION_REPEAT_COUPON
                : coupon.origin === COUPON_MAIL &&
                  coupon.couponMailType === promotionEmailCouponType.TPK
                ? promotionOriginType.COUPON_MAIL
                : promotionOriginType.COMMON

        // Get promotions in cart
        const promotionsInCart: Array<ClickAndCollectPromotion> = promotionsAppliedInCart(
            products
        )

        const isCodeAlreadyUsedInCart = promotionsInCart.some(
            promo => promo.couponCode === coupon.id
        )

        // Verify if cart contains common promotion
        // exclude the tpk promotions from the common
        // exclude the CHAUDBK promotions from the common
        const hasCommonPromotion = !!products.find(
            p =>
                p.promotionOrigin === promotionOriginType.COMMON &&
                (!p.promotions ||
                    !p.promotions.some(
                        promotion =>
                            promotion.couponCode &&
                            (isTuPreferesKingCoupon(promotion.couponCode) ||
                                promotion.couponCode ===
                                    COUPON_CODES.FREE_HOT_DRINK ||
                                promotion.couponCode ===
                                    COUPON_CODES.FREE_PRODUCT)
                    ))
        )
        // Verify if the cart contains kingdom points promotions
        const hasKingdomPointsPromotions = !!promotionsInCart.find(
            promotion =>
                promotion.couponCode &&
                isKingdomPointsCoupon(promotion.couponCode)
        )
        // Verify if the cart contains kingdom points promotions
        const hasLotteryPromotions = !!promotionsInCart.find(
            promotion =>
                promotion.couponCode && isLotteryCoupon(promotion.couponCode)
        )
        // Verify if cart contains operation repeat promotions
        const hasOperationRepeatPromotion = !!products.find(
            p =>
                p.promotionOrigin ===
                promotionOriginType.OPERATION_REPEAT_COUPON
        )
        // Verify if cart contains global promotions (ex: -10%)
        const hasOrderDiscountPromotion = promotions && promotions.length > 0

        const countTPKPromotions = promotionsInCart.filter(
            promotion =>
                promotion.couponCode &&
                isTuPreferesKingCoupon(promotion.couponCode)
        ).length
        const hasTPKPromotions = !!countTPKPromotions
        const maxSimultaneousTPK = 5
        const hasMaxTPK = countTPKPromotions >= maxSimultaneousTPK

        // Must consider empty cart cases, else values will be false when they shouldn't
        const isSameGenericCouponAlreadyInCart =
            promotionsInCart.some(
                promotionInCart =>
                    promotionInCart.operationCode &&
                    promotionInCart.operationCode === coupon.operationCode
            ) || promotionsInCart.length === 0
        const isCumulativeCouponAlreadyInCart =
            promotionsInCart.some(
                promotionInCart => promotionInCart.isCumulative
            ) || promotionsInCart.length === 0

        // GET PROMOTION TYPE
        // Check if promotion is from kingdom points
        const isKingdomPointsPromotion = coupon.origin === KINGDOM_POINTS
        // Check if promotion is from operation repeat
        const isOperationRepeatPromotion =
            coupon.origin === OPERATION_REPEAT_COUPON
        // Check if promotion is from lottery gain
        const isLotteryOperationPromotion = coupon.origin === LOTTERY_OPERATION
        // Check if promotion is of ORDER type
        const isOrderPromotion =
            promoType && promoType.includes(promotionBeginningType.ORDER)
        // Check if promotion is on delivery fees
        isDeliveryFeesPromotion =
            promotion.productIds && promotion.productIds.includes(fees?.id)
        const isCommonPromotion = promotionOrigin === promotionOriginType.COMMON
        const isTPKPromotion =
            coupon.couponMailType === promotionEmailCouponType.TPK
        const isRandomPromotion =
            coupon?.operationRepeatAttributionType ===
            OPERATION_REPEAT_ATTRIBUTION_TYPE.RANDOM
        const isFreeHotDrinkPromotion =
            coupon.id === COUPON_CODES.FREE_HOT_DRINK
        const isFreeHotDrinkPromotionAlreadyInCart = promotionsInCart.some(
            promotionInCart =>
                promotionInCart.couponCode === COUPON_CODES.FREE_HOT_DRINK
        )

        const isFreeProductPromotion = coupon.id === COUPON_CODES.FREE_PRODUCT
        const isFreeProductPromotionAlreadyInCart = promotionsInCart.some(
            promotionInCart =>
                promotionInCart.couponCode === COUPON_CODES.FREE_PRODUCT
        )

        // Check if promotion is a generic coupon
        const isGenericCoupon = coupon.origin === GENERIC_COUPON

        /* Rules to add a promotion:
            - You can only add one promotion
            exceptions :
            - You can add multiple operation repeat promotions, but you can't combine them with other types of promotions (except kingdom points promotions)
            - You can add multiple kingdom points promotions, but you can't combine them other types of promotions (except operation repeat promotions)
            - You can add multiple generic coupon operation, if they are from the same generic coupon
            - All cumulative coupons count for one coupon
        */

        const isCumulationAuthorized =
            isRandomPromotion ||
            isOperationRepeatPromotion ||
            isKingdomPointsPromotion ||
            isLotteryOperationPromotion ||
            isTPKPromotion ||
            hasLotteryPromotions ||
            isSameGenericCouponAlreadyInCart ||
            isCumulativeCouponAlreadyInCart ||
            (isFreeHotDrinkPromotion &&
                !isFreeHotDrinkPromotionAlreadyInCart) ||
            (isFreeProductPromotion && !isFreeProductPromotionAlreadyInCart)

        const hasMaxPromo =
            !isKingdomPointsPromotion &&
            (hasOrderDiscountPromotion ||
                (isOrderPromotion && hasLotteryPromotions) ||
                (hasCommonPromotion &&
                    !isGenericCoupon &&
                    !hasLotteryPromotions &&
                    !isRandomPromotion) ||
                (hasOperationRepeatPromotion && !isCumulationAuthorized) ||
                (hasKingdomPointsPromotions && !isCumulationAuthorized) ||
                (isGenericCoupon && !isCumulationAuthorized) ||
                (isTPKPromotion && hasMaxTPK) ||
                (isTPKPromotion && hasOperationRepeatPromotion) ||
                (isTPKPromotion && hasCommonPromotion) ||
                (hasTPKPromotions && isOperationRepeatPromotion) ||
                (hasTPKPromotions && isCommonPromotion) ||
                (isTPKPromotion && isCodeAlreadyUsedInCart) ||
                (isFreeHotDrinkPromotion &&
                    isFreeHotDrinkPromotionAlreadyInCart) ||
                (isFreeProductPromotion && isFreeProductPromotionAlreadyInCart))

        const EcommPromo = {
            couponCode: coupon.id,
            idReboot: promotion.idReboot,
            promoType: promotion.promoType,
            promoValue: promotion.promoValue || 0,
            promotionId: coupon.promotionId,
            promotionName: promotion.name,
            threshold: promotion.threshold,
            cumulative: coupon.isCumulative,
            operationCode: coupon.operationCode
        }

        const isMenu = menuIds && menuIds.length > 0
        const isRequiredMenu = requiredMenuIds && requiredMenuIds.length > 0
        const isProduct = productIds && productIds.length > 0
        const isRequiredProduct =
            requiredProductIds && requiredProductIds.length > 0
        const hasRequiredItems = isRequiredMenu || isRequiredProduct

        if (hasMaxPromo) {
            error = 'clickandcollect.discounts.max'
            isKingdomPointsCoupon(coupon.id) &&
                dispatch(untransformPoints(coupon.id))
        } else if (isOrderPromotion) {
            type = ORDER
            dispatch(addPromotionToCart(EcommPromo))
        } else {
            let id = '',
                label = ''

            //$FlowFixMe
            const isGame = isDefined(coupon.gameId)
            if (isDeliveryFeesPromotion) {
                dispatch(setDeliveryFees(EcommPromo))
            } else if (isGame) {
                const game =
                    gamesById &&
                    //$FlowFixMe
                    coupon.gameId &&
                    gamesById[coupon.gameId.toString()]

                const currentOperationRepeat =
                    operationsRepeat &&
                    operationsRepeat.find(
                        e => e.code === coupon.operationRepeatCode
                    )

                if (!game || !coupon.id || !currentOperationRepeat) {
                    error = 'error.section.title'
                } else {
                    const purchasableItem = operationGameToPurchasable(
                        currentOperationRepeat.code,
                        coupon.id,
                        game,
                        //$FlowFixMe
                        coupon.gameId?.toString()
                    )
                    type = GAME
                    dispatch(addProductToCart(purchasableItem))
                }
            } else if (isMenu || isProduct) {
                const items = dispatch(
                    getItemsFromIds(
                        //$FlowFixMe
                        isMenu ? menuIds : productIds,
                        isMenu ? MENU : PRODUCT
                    )
                )
                const itemsRequired = dispatch(
                    getItemsFromIds(
                        //$FlowFixMe
                        isRequiredMenu ? requiredMenuIds : requiredProductIds,
                        isRequiredMenu ? MENU : PRODUCT
                    )
                )
                if (isRequiredMenu || isRequiredProduct) {
                    switch (itemsRequired.length) {
                        case 0:
                            error = 'clickandcollect.discounts.unavailable'
                            isKingdomPointsCoupon(coupon.id) &&
                                dispatch(untransformPoints(coupon.id))
                            break
                        case 1:
                            requiredType = isRequiredMenu ? MENU : PRODUCT
                            if (itemsRequired[0].available === false) {
                                error = 'clickandcollect.discounts.unavailable'
                                isKingdomPointsCoupon(coupon.id) &&
                                    dispatch(untransformPoints(coupon.id))
                            } else {
                                id = itemsRequired[0].id
                                label = itemsRequired[0].name
                                hasOptions =
                                    isRequiredMenu === PRODUCT &&
                                    (itemsRequired[0].options.length > 0 ||
                                        itemsRequired[0].hasIce ||
                                        itemsRequired[0].hasPickUpLater)
                                if (
                                    isRequiredMenu === PRODUCT &&
                                    id &&
                                    !hasOptions
                                ) {
                                    const product = getProductById(
                                        getState().ecommerceCatalogState,
                                        id
                                    )
                                    if (product) {
                                        const purchasableProduct = purchasable(
                                            product,
                                            undefined,
                                            EcommPromo,
                                            promotionOrigin
                                        )
                                        purchasableProduct &&
                                            dispatch(
                                                addProductToCart(
                                                    purchasableProduct
                                                )
                                            )
                                    }
                                }
                            }
                            break
                        default:
                            requiredType = isRequiredMenu ? MENU : PRODUCT
                            listRequiredItems = itemsRequired
                    }
                }

                switch (items.length) {
                    case 0:
                        error = 'clickandcollect.discounts.unavailable'
                        isKingdomPointsCoupon(coupon.id) &&
                            dispatch(untransformPoints(coupon.id))
                        break
                    case 1:
                        type = isMenu ? MENU : PRODUCT
                        if (
                            items[0].available === false ||
                            (listItems &&
                                (listItems.every(item => !item.available) ||
                                    !listItems.length))
                        ) {
                            error = 'clickandcollect.discounts.unavailable'
                            isKingdomPointsCoupon(coupon.id) &&
                                dispatch(untransformPoints(coupon.id))
                        } else {
                            id = items[0].id
                            label = items[0].name
                            hasOptions =
                                isProduct &&
                                (items[0].options.length > 0 ||
                                    items[0].hasIce ||
                                    items[0].hasPickUpLater)
                            if (isProduct && id && !hasOptions) {
                                const product = getProductById(
                                    getState().ecommerceCatalogState,
                                    id
                                )

                                if (
                                    (hasRequiredItems &&
                                        fromNextPromotionScreen &&
                                        product) ||
                                    (product && !hasRequiredItems)
                                ) {
                                    const purchasableProduct = purchasable(
                                        product,
                                        undefined,
                                        EcommPromo,
                                        promotionOrigin
                                    )
                                    purchasableProduct &&
                                        dispatch(
                                            addProductToCart(purchasableProduct)
                                        )
                                }
                            }
                        }
                        break
                    default:
                        type = isMenu ? MENU : PRODUCT
                        listItems = items
                }
            }
            id && dispatch(setCurrentItemInfos({ id, name: label }))
        }
        if (
            hasRequiredItems &&
            listItems &&
            (listItems.every(item => !item.available) || !listItems.length)
        ) {
            //if we have requiredItems to add before the actual promotion items, but the actual promotion items are not available,
            //we show an error instead of asking the user to add the required items and then being stuck on the screen to select the actual promotion items
            error = 'clickandcollect.discounts.unavailable'
            isKingdomPointsCoupon(coupon.id) &&
                dispatch(untransformPoints(coupon.id))
        }

        return {
            listItems,
            type,
            error,
            hasOptions,
            isDeliveryFeesPromotion,
            listRequiredItems,
            requiredType
        }
    }
}

const cancelOrder = (
    id: string,
    token: string,
    deviceCheckPayload: DeviceCheck,
    target?: 'deliveryOrders' | 'cacOrders'
) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const wsEndpoint = `${endpoints.ecommerce.orders}/${id}/cancellation?token=${token}`
            const client = target === 'deliveryOrders' ? deliveryClient : ws
            const response = await client.post(wsEndpoint, deviceCheckPayload)
            if (response && response.data) {
                dispatch(actionUpdateOrder({ ...response.data, token }))
            }
        } catch (error) {
            if (error.request) {
                throw errors.NETWORK
            }
            throw error
        }
    }
}

const cancelCoupon = (couponCode: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { restaurant, collectMode } = getState().ecommerceOrderState || {}
        try {
            if (isKingdomPointsCoupon(couponCode)) {
                dispatch(untransformPoints(couponCode))
            }

            // Add a security check for backend BE-7749
            if (restaurant && restaurant.id) {
                await ws.delete(endpoints.ecommerce.removeCoupon, {
                    data: {
                        code: couponCode,
                        restaurant: restaurant.id,
                        pickUpType: collectMode
                    }
                })
            }
        } catch (error) {
            console.error('Error cancel coupon')
        }
    }
}

const cancelPayment = () => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const {
                cacOrders,
                currentToken,
                deliveryOrders
            } = getState().ecommerceOrderState

            const currentOrder: any = getCurrentOrder(
                cacOrders,
                deliveryOrders,
                currentToken
            )

            const wsEndpoint = `${endpoints.ecommerce.orders}/${
                currentOrder.id
            }/payment/cancel?token=${currentToken || ''}`

            const response = await ws.post(wsEndpoint)
            if (response && response.data) {
                dispatch(
                    actionUpdateOrder({ ...response.data, token: currentToken })
                )
            }
        } catch (error) {
            if (error.request) {
                throw errors.NETWORK
            }
            throw error
        }
    }
}

const fetchDrivers = (orderId: string, orderToken: string) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await deliveryClient.get(
                `${endpoints.ecommerce.orders}/${orderId}/driver-status?token=${orderToken}`
            )
            if (response && response.data) {
                response.data.orderJobs &&
                    response.data.orderJobs.length > 0 &&
                    dispatch(actionFetchDrivers(response.data.orderJobs))
            }
        } catch (error) {
            console.error('Error fetching driver status', error)
            throw error
        }
    }
}

const sendPaymentLog = (event: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const {
            currentToken,
            cacOrders,
            deliveryOrders,
            ecommerceMode
        } = getState().ecommerceOrderState
        const order =
            getCurrentOrder(cacOrders, deliveryOrders, currentToken) ||
            getMostRecentOrder({ ...cacOrders, ...deliveryOrders })
        const { id, token } = order || {}

        if (id && token) {
            const client = getEcommerceAxiosClient(ecommerceMode)
            client
                .get(
                    `${endpoints.ecommerce.orders}/${id}/payment/event?token=${token}&event=${event}`
                )
                .catch(error =>
                    console.error('Error sending payment log to backend', error)
                )
        }
    }
}

const setReceiptUrl = (
    orderId: string,
    target: 'deliveryOrders' | 'cacOrders'
) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const { data } = await ws.get(
                `${endpoints.kingdom.order.receipt}${orderId}`
            )
            dispatch(actionSetReceiptUrl(data, orderId, target))
        } catch (error) {
            console.error('Error fetching receipt', error)
            throw error
        }
    }
}

//Fetch Adyen Payment Methods
const fetchAdyenPaymentMethods = (restaurantCode: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { token } = getState().userState
        const response = await ws.get(
            `${endpoints.ecommerce.adyenPaymentMethods}?restaurant=${restaurantCode}`,
            {
                headers: token ? { RenewToken: true, Authorization: token } : {}
            }
        )

        if (response && response.data) {
            dispatch(actionAdyenPaymentMethods(response.data))
        }
    }
}

const sendAdyenPayment = ({
    paymentMethod,
    browserInfo,
    storePaymentMethod = false,
    channel,
    returnUrl,
    customerEmail
}: {
    paymentMethod: PaymentMethod,
    browserInfo?: BrowserInfo,
    storePaymentMethod?: boolean,
    channel: string,
    returnUrl?: string,
    customerEmail?: string
}) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const {
            cacOrders,
            currentToken,
            deliveryOrders
        } = getState().ecommerceOrderState

        const currentOrder: any = getCurrentOrder(
            cacOrders,
            deliveryOrders,
            currentToken
        )

        const isDelivery =
            getState().ecommerceOrderState.ecommerceMode === DELIVERY_IN_APP ||
            getState().ecommerceOrderState.collectMode === DELIVERY_IN_APP

        const isTableOrder =
            getState().ecommerceOrderState.ecommerceMode === TABLE_ORDER ||
            getState().ecommerceOrderState.collectMode === TABLE_ORDER

        const client = isDelivery ? deliveryClient : ws

        const response = await client.post(
            `${endpoints.ecommerce.orders}/v2/${currentOrder.id ?? ''}/payment`,
            {
                orderId: currentOrder.id,
                token: currentToken,
                paymentMethod,
                browserInfo,
                storePaymentMethod,
                channel,
                returnUrl,
                customerEmail
            }
        )

        if (response && response.data) {
            try {
                dispatch(
                    actionAdyenPaymentResponse(response.data?.paymentResponse)
                )

                const resultCode = response.data?.paymentResponse?.resultCode
                const orderStatus = response.data?.order?.orderStatus
                dispatch(
                    actionUpdateOrder({
                        ...response.data?.order,
                        token: currentToken
                    })
                )

                if (orderStatus && orderStatus.toUpperCase() === 'REJECTED') {
                    dispatch(setError({ message: 'cart.order.rejected' }))
                }

                if (resultCode && resultCode.toUpperCase() === 'AUTHORISED') {
                    if (isDelivery) {
                        dispatch(
                            prepareOrder({
                                token: currentToken,
                                pickUpType: 'DELIVERY_IN_APP'
                            })
                        )
                    }

                    if (isTableOrder) {
                        try {
                            dispatch(
                                prepareOrder({
                                    token: currentToken,
                                    pickUpType: TABLE_ORDER,
                                    pickUpInfo: currentOrder.pickUpInfo
                                })
                            )
                        } catch (error) {
                            throw error
                        }
                    }

                    dispatch(
                        actionAdyenRedirection({
                            orderId: currentOrder.id,
                            orderToken: currentToken
                        })
                    )

                    dispatch(resetCart())
                }
            } catch (error) {
                throw error
            }
        }
    }
}

const sendAdyenPaymentValidation = ({ details }: any) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const {
            cacOrders,
            currentToken,
            deliveryOrders
        } = getState().ecommerceOrderState

        const currentOrder: any = getCurrentOrder(
            cacOrders,
            deliveryOrders,
            currentToken
        )

        const isDelivery =
            getState().ecommerceOrderState.ecommerceMode === DELIVERY_IN_APP ||
            getState().ecommerceOrderState.collectMode === DELIVERY_IN_APP

        const isTableOrder =
            getState().ecommerceOrderState.ecommerceMode === TABLE_ORDER ||
            getState().ecommerceOrderState.collectMode === TABLE_ORDER

        const client = isDelivery ? deliveryClient : ws

        const response = await client.post(
            `${endpoints.ecommerce.orders}/v2/${currentOrder.id ??
                ''}/payment/validation`,
            {
                orderId: currentOrder.id,
                token: currentToken,
                details
            }
        )

        if (response && response.data) {
            dispatch(actionUpdateOrder(response.data))

            if (response.data.orderStatus === errors.PAYMENT_ERROR) {
                throw errors.PAYMENT_ERROR
            }

            if (isDelivery) {
                dispatch(
                    prepareOrder({
                        token: currentToken,
                        pickUpType: 'DELIVERY_IN_APP'
                    })
                )
            }

            if (isTableOrder) {
                try {
                    dispatch(
                        prepareOrder({
                            token: currentToken,
                            pickUpType: TABLE_ORDER,
                            pickUpInfo: currentOrder.pickUpInfo
                        })
                    )
                } catch (error) {
                    throw error
                }
            }

            dispatch(
                actionAdyenRedirection({
                    orderId: currentOrder.id,
                    orderToken: currentToken
                })
            )

            dispatch(resetCart())
        }
    }
}

const sendAdyenApplePayValidation = ({
    merchantId,
    merchantName,
    appleEndpointUrl,
    originApplePay
}: {
    merchantId: string,
    merchantName: string,
    appleEndpointUrl: string,
    originApplePay?: string
}) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const {
            cacOrders,
            currentToken,
            deliveryOrders
        } = getState().ecommerceOrderState

        const currentOrder: any = getCurrentOrder(
            cacOrders,
            deliveryOrders,
            currentToken
        )

        const isDelivery =
            getState().ecommerceOrderState.ecommerceMode === DELIVERY_IN_APP

        const client = isDelivery ? deliveryClient : ws

        const response = await client.post(
            `${endpoints.ecommerce.orders}/v2/${currentOrder.id ??
                ''}/payment/applepay/adyen/validation`,
            {
                token: currentToken,
                merchantId,
                merchantName,
                appleEndpointUrl,
                originApplePay
            }
        )

        if (response && response.data) {
            return response.data
        }
    }
}

export {
    addMultipleProductsToCart,
    addProductToCart,
    addPromotionToCart,
    cancelOrder,
    cancelPayment,
    discard,
    fetchAdyenPaymentMethods,
    fetchCartPriceEdges,
    fetchDrivers,
    fetchLastCcOrder,
    fetchOrder,
    fetchOrders,
    fetchRestaurant,
    fillCartWithMultipleProducts,
    getPaymentUrl,
    handleMissingProducts,
    isParkingOpen,
    isTableServiceAvailable,
    oneClickPayment,
    prepareOrder,
    processCouponNavigation,
    removeCurrentOrderToken,
    removeProductFromCart,
    removePromotionFromCart,
    resetCart,
    resetOrder,
    resetCurrentItem,
    sendAdyenApplePayValidation,
    sendAdyenPayment,
    sendAdyenPaymentValidation,
    sendCurrentOrder,
    sendPaymentLog,
    setAdyenRedirection,
    setRandomCoupon,
    setCartToast,
    setCollectMode,
    setCurrentItem,
    setCurrentItemInfos,
    setCurrentSuggestions,
    setDeliveryAddress,
    setDeliveryInfos,
    setEcommerceMode,
    setError,
    setLoader,
    setReceiptUrl,
    setRestaurant,
    setUserInfos,
    updateDeliveryRestaurant,
    updateProductInCart,
    waitForOrderStatusChange,
    setHideNeptingCloseButton
}

export type Action =
    | ActionSetError
    | ActionSetRestaurant
    | ActionSetEcommerceMode
    | ActionSetDeliveryAddress
    | ActionSetDeliveryFees
    | ActionSetCollectMode
    | ActionUpdateCart
    | ActionAddProductToCart
    | ActionRemoveProductFromCart
    | ActionSetUserInfos
    | ActionSetCurrentItem
    | ActionSetCurrentItemInfos
    | ActionResetCurrentItem
    | ActionSetCartToast
    | ActionSetRandomCoupon
    | ActionResetCart
    | ActionResetOrder
    | ActionUpdateOrders
    | ActionHandleMissingProducts
    | ActionUpdateCurrentOrder
    | ActionUpdateOrder
    | ActionHasLastOrder
    | ActionOrderRejected
    | ActionSetPromotion
    | ActionHandleCoupon
    | ActionUpdateOrb
    | ActionOrderLogout
    | ActionDiscardOrder
    | ActionSetCurrentSuggestions
    | ActionParkingOpen
    | ActionFetchPriceEdges
    | ActionFetchDrivers
    | ActionUpdateOrderOrbStatus
    | ActionSetReceiptUrl
    | ActionAdyenPaymentMethods
    | ActionAdyenPaymentResponse
    | ActionRemoveCurrentOrderToken
    | ActionSetDeliveryInfos
    | ActionSetLoader
    | ActionAdyenRedirection
    | ActionSetHideNeptingCloseButton
